import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { DEFAULT_SCREEN_CONFIGS } from '../../constants/slot-screen-defaults';
import { GeneratorGallery } from './GeneratorGallery';
import { GeneratorPromptBar } from './GeneratorPromptBar';
import { GeneratorFullView } from './GeneratorFullView';
import type { SlotScreenId } from '@core/types/slot-screen-generator.types';

export function ComponentGeneratorView() {
  const generatorViewComponentId = useSlotScreenGeneratorStore(s => s.generatorViewComponentId);
  const activeScreenId = useSlotScreenGeneratorStore(s => s.activeScreenId);
  const screenComponents = useSlotScreenGeneratorStore(s => s.screenComponents);
  const generatorFullViewIndex = useSlotScreenGeneratorStore(s => s.generatorFullViewIndex);
  const closeGeneratorView = useSlotScreenGeneratorStore(s => s.closeGeneratorView);

  if (!generatorViewComponentId) return null;

  const components = screenComponents[activeScreenId];
  const component = components?.find(c => c.id === generatorViewComponentId);
  if (!component) return null;

  const screenConfig = DEFAULT_SCREEN_CONFIGS[activeScreenId as SlotScreenId];
  const screenName = screenConfig?.name ?? activeScreenId;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-zinc-950">
      {/* Header bar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-zinc-800 bg-zinc-900/80 flex-shrink-0">
        <button
          onClick={closeGeneratorView}
          className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          <ChevronLeft size={16} />
          Back to Canvas
        </button>
        <div className="flex-1 text-center">
          <span className="text-sm font-medium text-zinc-200">{component.name}</span>
          <span className="text-xs text-zinc-500 ml-2">&mdash; {screenName}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          <span>{component.width} x {component.height}</span>
          <span>z:{component.zIndex}</span>
        </div>
      </div>

      {/* Gallery (scrollable) */}
      <div className="flex-1 overflow-y-auto">
        <GeneratorGallery component={component} />
      </div>

      {/* Prompt bar (sticky bottom) */}
      <GeneratorPromptBar component={component} />

      {/* Full-view overlay (conditional) */}
      {generatorFullViewIndex !== null && (
        <GeneratorFullView component={component} />
      )}
    </div>
  );
}
